import type { ListingColumn } from './types';

export type SortDir = 'asc' | 'desc' | null;

/**
 * The pt-BR collator used for text sorting. `sensitivity: 'base'` folds
 * accents and case together so "Álvaro" sorts next to "Alvaro" (and before
 * "Bravo") instead of landing after "Zeca" the way a raw `<` comparison of
 * UTF-16 code units would. `numeric: true` makes embedded numbers order
 * naturally ("item 2" before "item 10").
 */
const collator = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });

/** Normalizes a finite number to itself, or `null` for `NaN`. */
function finiteOrNull(n: number): number | null {
  return Number.isNaN(n) ? null : n;
}

/**
 * Parses a date-shaped string into epoch millis. Maps the pt-BR
 * `dd/mm/yyyy` shape to an unambiguous ISO date first, because `Date` parses
 * it inconsistently across engines; everything else goes straight to
 * `Date.parse`.
 */
function parseDateString(raw: string): number | null {
  const brMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
  const parsed = brMatch ? Date.parse(`${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`) : Date.parse(raw);
  return finiteOrNull(parsed);
}

/**
 * Coerces an arbitrary scalar into epoch milliseconds for date sorting.
 * Accepts a `Date`, an epoch number, or any string `Date` can parse (ISO or
 * the pt-BR `dd/mm/yyyy` shape). Returns `null` when the value is empty or
 * unparseable so it sorts to the end alongside genuine nulls.
 */
function toEpoch(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return finiteOrNull(value.getTime());
  if (typeof value === 'number') return finiteOrNull(value);
  return parseDateString(String(value).trim());
}

/**
 * Strips digit-group separators and currency glyphs from a formatted number
 * string ("R$ 1.180.000,50" → "1180000.50"), returning the parsed value or
 * `null` when nothing numeric remains.
 */
function parseFormattedNumber(value: string): number | null {
  const cleaned = value
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');
  if (cleaned === '' || cleaned === '-') return null;
  return finiteOrNull(Number(cleaned));
}

/**
 * Coerces an arbitrary scalar into a comparable number. Digit-group
 * separators and currency glyphs ("R$ 1.180.000,50") are stripped so a
 * formatted-currency scalar still orders numerically. Returns `null` when
 * nothing numeric remains so it sorts last.
 */
function toNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return finiteOrNull(value);
  return parseFormattedNumber(String(value));
}

/**
 * Extracts the comparable scalar for a column: the `sortAccessor` projection
 * when present, otherwise `row[key]`.
 */
export function columnScalar<T>(col: ListingColumn<T>, item: T): unknown {
  if (col.sortAccessor) return col.sortAccessor(item);
  return (item as Record<string, unknown>)[col.key];
}

/**
 * Whether a scalar counts as empty for sorting: `null`, `undefined`, or the
 * empty string. Empty values always sort last regardless of direction.
 */
function isEmpty(value: unknown): boolean {
  return value == null || value === '';
}

/**
 * Compares two already-projected nullable numbers, nulls last. Shared by the
 * numeric and date paths so neither repeats the null bookkeeping.
 */
function compareNullableNumbers(na: number | null, nb: number | null): number {
  if (na == null && nb == null) return 0;
  if (na == null) return 1;
  if (nb == null) return -1;
  return na - nb;
}

/**
 * Direction-agnostic comparison of two non-empty scalars for a given type.
 * Each branch delegates its projection + null handling to a helper, keeping
 * this a flat three-way dispatch.
 */
function baseCompare(type: 'text' | 'number' | 'date', rawA: unknown, rawB: unknown): number {
  if (type === 'number') return compareNullableNumbers(toNumber(rawA), toNumber(rawB));
  if (type === 'date') return compareNullableNumbers(toEpoch(rawA), toEpoch(rawB));
  return collator.compare(String(rawA), String(rawB));
}

/**
 * Nulls-last comparison for a pair where at least one side is empty. Projecting
 * each side to `null`/`0` reuses the shared nulls-last number comparison so an
 * empty value never leads a descending sort.
 */
function compareWithEmpty(rawA: unknown, rawB: unknown): number {
  return compareNullableNumbers(isEmpty(rawA) ? null : 0, isEmpty(rawB) ? null : 0);
}

/**
 * Type-aware, nulls-last comparator shared by `ListingTable` and
 * `useTriStateSort`. `null`/`undefined`/`''` always sort to the end
 * regardless of direction; two nulls tie. Text uses the pt-BR collator,
 * numbers subtract, dates compare epoch millis.
 */
export function compareByColumn<T>(col: ListingColumn<T>, a: T, b: T, dir: SortDir): number {
  if (!dir) return 0;
  const rawA = columnScalar(col, a);
  const rawB = columnScalar(col, b);

  // Nulls last on BOTH directions — decided before the direction sign is
  // applied, so an empty value never leads a descending sort.
  if (isEmpty(rawA) || isEmpty(rawB)) return compareWithEmpty(rawA, rawB);

  const sign = dir === 'asc' ? 1 : -1;
  return sign * baseCompare(col.sortType ?? 'text', rawA, rawB);
}
